import React, {useContext, useEffect, useState} from "react";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import {FormattedMessage} from "react-intl";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Modal from "@amzn/meridian/modal";
import useCapabilityContext from "../global/context/useCapabilityContext";
import {RouteStageContext} from "./context/RouteStageContext";
import {STAGE_TASK_PLAN_STATUS} from "../util/EntityStatusManager";
import { isRouteAdjustmentEnabled } from "../util/Util";


const PlanManagementView = () => {

    const {configViewModel, configViewModelRef} = useCapabilityContext()
    const {viewModel, dispatch} = useContext(RouteStageContext)
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingConfirmationData, setPendingConfirmationData] = useState(null);

    useEffect(() => {
        const config = configViewModelRef.current;
        if (config.stationConfig.initialised) {
            getStagePlanTasksData(config)
        }
    }, [configViewModel.stationConfig.initialised]);

    useEffect(() => {
        if (viewModel.showPendingConfirmation) {
            setShowConfirmationModal(true);
            setPendingConfirmationData(viewModel.pendingConfirmationData);
        }
    }, [viewModel.showPendingConfirmation, viewModel.pendingConfirmationData]);

    const getStagePlanTasksData = (config) => {
        dispatch({
            type: 'LOAD_STAGE_PLAN_TASKS',
            data: {
                baseUrl: config.baseUrl,
                stationConfig: config.stationConfig,
                dispatcher: dispatch
            }
        });
    };

    const planList = viewModel.stageTaskPlanList

    const getTableHeader = () => {
        return(
            <TableRow header={true}>
                <TableCell>
                    <FormattedMessage id="planManagement.cycle" defaultMessage="Cycle"/>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="planManagement.node" defaultMessage="Node"/>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="planManagement.plan_id" defaultMessage="Plan id"/>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="planManagement.created_date" defaultMessage="Created date"/>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="planManagement.status" defaultMessage="Status"/>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="planManagement.update_plan_status" defaultMessage="Update plan status"/>
                </TableCell>
            </TableRow>
        )
    }

    const updatePlanStatus = (plan) => {
        const config = configViewModelRef.current;
        const isPlanBeingActivated = plan.status === "HOLD";
        isRouteAdjustmentEnabled().then(isEnabled => {
            dispatch({
                type: 'UPDATE_STAGE_TASK_PLAN_STATUS',
                data: {
                    baseUrl: config.baseUrl,
                    stationConfig: config.stationConfig,
                    dispatcher: dispatch,
                    planId: plan.stageTaskPlanId,
                    status: getStatusToBeUpdated(plan.status),
                    forceUpdate: !isEnabled || !isPlanBeingActivated,
                    cycleName: plan.planCycle
                }
            });
        });
    }

    const getTableRows = () => {
        return (
            planList.map(plan => (
                <TableRow key={plan.stageTaskPlanId}>
                    <TableCell header={true} key={`${plan.planCycle}${plan.stageTaskPlanId}`}> {plan.planCycle} </TableCell>
                    <TableCell key={`${plan.nodeId}${plan.stageTaskPlanId}`}> {plan.nodeId} </TableCell>
                    <TableCell key={`StagePlanId${plan.stageTaskPlanId}`}> {plan.stageTaskPlanId} </TableCell>
                    <TableCell key={`${plan.planDay}${plan.stageTaskPlanId}`}> {plan.planDay} </TableCell>
                    <TableCell key={`${plan.status}${plan.stageTaskPlanId}`}> {getPlanStatusString(plan.status)} </TableCell>
                    <TableCell key={`UpdateButton${plan.stageTaskPlanId}`}>
                        <Button onClick={() => updatePlanStatus(plan)}>
                            {getUpdateButtonStatusString(plan.status)}
                        </Button>
                    </TableCell>
                </TableRow>
            ))
        )
    }

    const getPlanStatusString = (status) => {
        if (status === STAGE_TASK_PLAN_STATUS.ASSIGNABLE) {
            return (<FormattedMessage id="planManagement.active_plan" defaultMessage="Active"/>)
        } else {
            return (<FormattedMessage id="planManagement.inactive_plan" defaultMessage="Inactive"/>)
        }
    }

    const getUpdateButtonStatusString = (status) => {
        if (status === STAGE_TASK_PLAN_STATUS.ASSIGNABLE) {
            return (<FormattedMessage id="planManagement.deactivate_plan" defaultMessage="Deactivate status"/>)
        } else {
            return (<FormattedMessage id="planManagement.activate_plan" defaultMessage="Activate status"/>)
        }
    }

    const getStatusToBeUpdated = (currentStatus) => {
        if (currentStatus === STAGE_TASK_PLAN_STATUS.ASSIGNABLE) {
            return STAGE_TASK_PLAN_STATUS.HOLD
        } else {
            return STAGE_TASK_PLAN_STATUS.ASSIGNABLE
        }
    }

    const handleProceedAnyway = () => {
        const config = configViewModelRef.current;
        const currentPlan = viewModel.stageTaskPlanList.find(p => p.stageTaskPlanId === pendingConfirmationData.planId);
        const cycleName = currentPlan.planCycle;
        dispatch({
            type: 'UPDATE_STAGE_TASK_PLAN_STATUS',
            data: {
                baseUrl: config.baseUrl,
                stationConfig: config.stationConfig,
                dispatcher: dispatch,
                planId: pendingConfirmationData.planId,
                status: pendingConfirmationData.status,
                forceUpdate: true,  // Unblock Staging despite pending adjustments
                cycleName: cycleName
            }
        });
        
        setShowConfirmationModal(false);
        dispatch({ type: "CLEAR_PENDING_CONFIRMATION" });
    };

    const handleCancel = () => {
        setShowConfirmationModal(false);
        dispatch({ type: "CLEAR_PENDING_CONFIRMATION" });
    };

    const renderConfirmationModal = () => {
        if (!showConfirmationModal || !pendingConfirmationData) return null;
        
        return (
            <Modal width={500} title={<FormattedMessage id="planManagement.route_adjustment_title" defaultMessage="Route Adjustment Status"/>}
                open={showConfirmationModal} onClose={handleCancel}>
                <Row>
                    <FormattedMessage
                        id="planManagement.pending_tasks_message"
                        defaultMessage="There are {count} pending route adjustment tasks."
                        values={{ count: pendingConfirmationData.numberOfRemainingPackages }}
                    />
                </Row>
                <p><FormattedMessage id="planManagement.proceed_confirmation" defaultMessage="Do you want to proceed with activating the plan anyway?" /></p>

                <Row spacing="medium">
                    <Button onClick={handleProceedAnyway}><FormattedMessage id="planManagement.proceed_anyway" defaultMessage="Proceed Anyway" /></Button>
                    <Button onClick={handleCancel}><FormattedMessage id="planManagement.cancel" defaultMessage="Cancel" /></Button>
                </Row>
            </Modal>
        );
    };

    return (
        <Column>
            <Row width="100%" spacingInset="medium large none large">
            <Table headerRows={1} spacing="small" showStripes={true}>
                {getTableHeader()}
                {getTableRows()}
            </Table>
            </Row>
            {renderConfirmationModal()}
        </Column>
    )
}

export default PlanManagementView



// WEBPACK FOOTER //
// ./src/components/stageStatus/PlanManagementView.js